import {ALL_PRODUCT} from "data/constants";
import {CHANNELS, CONTENT_TYPES, PRODUCTS} from "data/products";
import {getVisibleChannels, isChannelPathHidden, isPartnerLike} from "utils/roles";

// ---------------------------------------------------------------------------
// The last editor location of each user. CoverBaker opens on it again, so a
// user who left IntelliJ IDEA on the Releases channel comes back to IntelliJ
// IDEA on the Releases channel, not to the default landing.
//
// The location is the editor URL: channel, product and content type in the
// path, and the selector params in the query. The URL is the record, not the
// editor state. A URL without `background=` opens the current default
// background of the gallery, and the kept copy does the same.
//
// Only the user moves the location (pageHasUserAction). A page that only
// opens a URL keeps nothing: a bookmark, a link, a URL from the browser
// history, or a tab that the browser restores.
//
// The location is kept in localStorage, one entry for each user email. It
// stays in the browser where the user made it. The index route opens it
// (Router.js). The Home button of the toolbar opens the default landing
// (resolveHomeLandingPath in libs/channelLanding.ts).
// ---------------------------------------------------------------------------

/** The facts about the viewer that decide which channels a location can open. */
export interface Viewer {
  role: string;
  /** The env-level flags of /api/auth/me. Of these, only `livestream` hides a channel. */
  featureFlags: {livestream?: boolean};
}

export const LAST_LOCATION_KEY_PREFIX = "neurobaker.lastLocation:";

/**
 * The query params that hold a selector position. The URL can hold other
 * params too: `render` for the server renderer and `preview` for the GDN news
 * preview. These are view modes, not positions, so they are not kept.
 */
const SELECTOR_PARAMS = [
  "set",
  "layout",
  "variant",
  // The tile number (libs/bgNumbering.ts), and the identity of a background
  // without one, as in an older record.
  "bg",
  "background",
  "privateBackground",
  "localization",
] as const;

/** `/<channel>/<product>/<contentType>`: the editor route with selectors in its URL. */
const EDITOR_PATH_RE = /^\/([^/]+)\/([^/]+)\/([^/]+)\/?$/;

/**
 * True when the editor opens this route for the viewer and stays on it. Each
 * check copies a guard that sends the user to a different route. Thus a kept
 * location cannot start a redirect.
 */
function canOpen(
  pathname: string,
  channel: string,
  product: string,
  type: string,
  viewer: Viewer
): boolean {
  // AppRoot sends a hidden channel path to the index.
  if (isChannelPathHidden(pathname, viewer.featureFlags)) return false;
  // The Channel dropdown shows only these channels, and the index route never
  // opened a different one.
  const visibleChannels = getVisibleChannels(CHANNELS, viewer.role, viewer.featureFlags);
  if (!visibleChannels.some((c: {value: string}) => c.value === channel)) return false;
  // App sends an unknown content type to /scene/<type>, for the old URL format.
  if (!CONTENT_TYPES.some((t: {value: string}) => t.value === type)) return false;
  if (product !== ALL_PRODUCT.value && !PRODUCTS.some((p: {value: string}) => p.value === product)) {
    return false;
  }
  // useCheckPermissions sends a partner on a product other than JetBrains to the index.
  if (isPartnerLike(viewer.role) && product !== "JB") return false;
  return true;
}

/**
 * The location to keep for this URL, or null when the URL is not an editor
 * position that the viewer can open again: a saved scene, an admin page, or a
 * channel that the viewer cannot see.
 */
export function lastLocationFor(pathname: string, search: string, viewer: Viewer): string | null {
  const match = EDITOR_PATH_RE.exec(pathname);
  if (!match) return null;
  const [, channel, product, type] = match;
  if (!canOpen(pathname, channel, product, type, viewer)) return null;

  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  for (const name of SELECTOR_PARAMS) {
    const value = params.get(name);
    if (value !== null) kept.set(name, value);
  }
  // An upload in the shared background slot is only in the page that made it.
  // Without the upload, `background=custom` selects an image that is not
  // there (see releaseCustomBgSelection in useInitialScene), so the default
  // background of the gallery applies. The server keeps a Point Cloud import,
  // and `privateBackground` loads it again.
  if (kept.get("background") === "custom" && !kept.has("privateBackground")) {
    kept.delete("background");
  }
  const query = kept.toString();
  return `/${channel}/${product}/${type}${query ? `?${query}` : ""}`;
}

function storageKey(email: string): string {
  return LAST_LOCATION_KEY_PREFIX + email.trim().toLowerCase();
}

function browserStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // A browser that blocks site storage throws on access.
    return null;
  }
}

/** The part of `navigator` that tells if the user acted on the page. Old browsers do not have it. */
interface UserActivationSource {
  userActivation?: {hasBeenActive: boolean};
}

/**
 * True when the user acted on this page: a click or a key press. Only a page
 * with a user action keeps its URL, because only the user changes a selector.
 * Until 2026-10-11 each visit to "/" ended on the same URL, so the browser
 * history offers that old landing. A page that only opened it replaced the
 * selectors that the user left.
 */
export function pageHasUserAction(
  nav: UserActivationSource | undefined = globalThis.navigator
): boolean {
  // A browser without the User Activation API keeps each URL, as before.
  return nav?.userActivation?.hasBeenActive ?? true;
}

/** Keep the location for the user. A browser without storage keeps nothing. */
export function saveLastLocation(
  email: string,
  location: string,
  storage: Storage | null = browserStorage()
): void {
  try {
    storage?.setItem(storageKey(email), location);
  } catch {
    // Full or blocked storage. The index then opens the default landing,
    // which is the behavior before the location was kept.
  }
}

/**
 * The kept location of the user, when the viewer can still open it, else
 * null. A role or a feature flag can change after the location was kept, so
 * the read checks the location again.
 */
export function readLastLocation(
  email: string,
  viewer: Viewer,
  storage: Storage | null = browserStorage()
): string | null {
  let url: URL;
  try {
    const stored = storage?.getItem(storageKey(email));
    if (!stored?.startsWith("/")) return null;
    // Parsed against a fixed origin: only the path and the query count, so a
    // changed entry cannot send the user to a different host.
    url = new URL(stored, "https://coverbaker.invalid");
  } catch {
    // The index route opens the default landing instead. It must not fail:
    // its catch sends the user to the login page.
    return null;
  }
  return lastLocationFor(url.pathname, url.search, viewer);
}
